const { produce, createStore } = window.theme.cart.functions;

/**
 * Shopify API returns an array of items with duplicate keys in case the same
 * item is both discounted, and an initiator of the discount. This function
 * removes the duplicate keys, adding a string of discount value to them.
 *
 * @param {Array} cart Cart object returned from Shopify API
 * @returns Cart object with items with amended keys
 */
const deDupeItemsInCart = (cart) => ({
  ...cart,
  items: cart.items.map((item) => {
    let newItem;
    if (
      item.line_level_discount_allocations &&
      item.line_level_discount_allocations.length > 0
    ) {
      newItem = {
        ...item,
        key: `${item.key}${item.line_level_discount_allocations.reduce(
          (discountsString, discount) => {
            return `${discountsString}_discount-${discount.amount}`;
          },
          '',
        )}`,
      };
    } else {
      newItem = item;
    }

    return newItem;
  }),
});

/**
 * Parses a fetch Response from a Shopify cart endpoint.
 * Always attempts to parse the JSON body first, since Shopify's cart
 * endpoints return a JSON error payload (with `description`/`message`)
 * even on non-2xx responses. If the response is not ok, throws an Error
 * built from that payload (or a generic message if the body isn't JSON,
 * e.g. a 500 HTML error page), so callers never try to read fields off
 * an unparsed/failed response.
 *
 * @param {Response} response
 * @returns {Promise<Object>}
 */
const parseCartResponse = async (response) => {
  let data = null;

  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }

  if (!response.ok) {
    const message =
      (data && (data.description || data.message)) ||
      `Cart request failed with status ${response.status}`;
    const error = new Error(message);
    error.cartResponse = data;
    throw error;
  }

  return data;
};

const fetchCart = async () => {
  try {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    const cart = await parseCartResponse(response);

    return deDupeItemsInCart(cart);
  } catch (e) {
    throw new Error(`Could not update cart: "${e.message}"`);
  }
};

const clearCart = async () => {
  try {
    const response = await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
      method: 'POST',
    });

    return await parseCartResponse(response);
  } catch (e) {
    throw new Error(`Could not clear cart: "${e.message}"`);
  }
};

const resetFreeShippingAnimation = () => {
  if (window.theme.cart.store.getState().products.total_price === 0)
    localStorage.removeItem('freeShippingAnimationShown');
};

const initiateStore = () => {
  const initialCartDataJson = document.getElementById('cart-data');
  const initialCartData = initialCartDataJson
    ? JSON.parse(initialCartDataJson.textContent)
    : {};

  const giftWrappingDataJson = document.getElementById(
    'cart-gift-wrapping-data',
  );
  const giftWrappingData = {
    giftWrapping: {
      ...(giftWrappingDataJson
        ? {
            ...JSON.parse(giftWrappingDataJson.textContent),
          }
        : {
            productId: null,
            wrapIndividually: false,
          }),
      statusBeingUpdated: false,
      messageBeingUpdated: false,
    },
  };

  window.theme.cart.store = createStore((set, get) => {
    /**
     * Tracks an in-flight update for a single line item (`ongoingUpdates.items[key]`).
     * Aborts any previous request for the same key, registers the new
     * controller, and marks the item as updating. Returns a `finish()`
     * callback that must be called in a `finally` block once the request
     * settles (success, failure, or abort) - it only clears the
     * updating/controller state if a *newer* call for the same key hasn't
     * already taken ownership, so overlapping requests never clobber each
     * other's pending state.
     */
    const beginLineItemUpdate = (key) => {
      if (get().ongoingUpdates.items[key]) {
        get().ongoingUpdates.items[key].abort();
        set(
          produce((draft) => {
            draft.ongoingUpdates.items[key] = null;
          }),
        );
      }

      const controller = new AbortController();

      set(
        produce((draft) => {
          if (!get().lineItemsBeingUpdated.includes(key)) {
            draft.lineItemsBeingUpdated.push(key);
          }
          draft.ongoingUpdates.items[key] = controller;
        }),
      );

      const owns = () => get().ongoingUpdates.items[key] === controller;

      const finish = () => {
        set(
          produce((draft) => {
            if (owns()) {
              draft.lineItemsBeingUpdated = draft.lineItemsBeingUpdated.filter(
                (k) => k !== key,
              );
              draft.ongoingUpdates.items[key] = null;
            }
          }),
        );
      };

      return { controller, signal: controller.signal, owns, finish };
    };

    return {
      ...giftWrappingData,
      error: null,
      lineItemsBeingUpdated: [],
      latestAddedProduct: null,
      noteBeingUpdated: false,
      ongoingUpdates: {
        items: {},
        note: null,
        giftWrappingMessage: null,
      },
      products: deDupeItemsInCart(initialCartData),
      recommendations: {
        enabled: false,
        intent: 'related',
        items: {},
      },
      productVariantsBeingAdded: [],
      clear: async () => {
        try {
          const products = await clearCart();

          set(
            produce((draft) => {
              draft.lineItemsBeingUpdated = [];
              draft.latestAddedProduct = null;
              draft.products = products;
              draft.recommendations = {};
              draft.productVariantsBeingAdded = [];
              draft.error = null;
            }),
          );
        } catch (e) {
          set(
            produce((draft) => {
              draft.error = e.message;
            }),
          );
          throw e;
        }
      },
      add: async (variants) => {
        if (!variants) throw new Error('Variant(s) must be specified');

        const items = (Array.isArray(variants) ? variants : [variants]).map(
          (item) => {
            if (Object.keys(item).find((key) => key.includes('properties'))) {
              return Object.entries(item).reduce((newItem, [key, value]) => {
                if (key.includes('properties') && value !== '') {
                  if (!newItem.properties) newItem.properties = {};
                  newItem.properties[
                    key.replace('properties[', '').replace(']', '')
                  ] = value;
                } else {
                  newItem[key] = value;
                }

                return newItem;
              }, {});
            }
            return item;
          },
        );

        const addedVariantIds = items.map((item) => item.id);

        set(
          produce((draft) => {
            // Merge rather than replace, so a concurrent add() in flight
            // doesn't lose track of the variant ids it's already tracking.
            draft.productVariantsBeingAdded = Array.from(
              new Set([...draft.productVariantsBeingAdded, ...addedVariantIds]),
            );
          }),
        );

        try {
          const addedResponseData = await fetch(
            `${window.Shopify.routes.root}cart/add.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ items }),
            },
          );
          const addedResponse = await parseCartResponse(addedResponseData);
          const products = await fetchCart();

          const prevItemsCount = get().products.item_count;

          set(
            produce((draft) => {
              draft.latestAddedProduct = addedResponse.items
                ? addedResponse.items[0]
                : null;
              draft.products = products;
              draft.error = null;
            }),
          );

          if (prevItemsCount !== products.item_count) {
            await get().updateGiftWrapping();
          }

          try {
            await get().updateRecommendations();
          } catch (e) {
            // Recommendations are non-critical - don't fail the add flow
            // over a failure to fetch them.
          }
        } catch (e) {
          set(
            produce((draft) => {
              draft.error = e.message;
              draft.productVariantsBeingAdded =
                draft.productVariantsBeingAdded.filter(
                  (id) => !addedVariantIds.includes(id),
                );
            }),
          );
          throw e;
        }
      },
      change: async (key, quantity = 0) => {
        if (!key) throw new Error('Line item key must be specified');

        const previousProducts = get().products;
        const { signal, owns, finish } = beginLineItemUpdate(key);

        try {
          // Need to use line and not the key due to Shopify
          // returning duplicate keys for same discounted items
          // TODO: Monitor API status, if the bug gets solved, replace with keys,
          // and remove dedupe function
          const line =
            get().products.items.findIndex((item) => item.key === key) + 1;
          const changedResponseData = await fetch(
            `${window.Shopify.routes.root}cart/change.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                line,
                quantity,
              }),
              signal,
            },
          );
          const changedResponse = await parseCartResponse(changedResponseData);

          // Sometimes change.js returns random line items with quantity of 0
          // in the items array (in particular when discounts are applied),
          // so we need to filter them out
          let products = {
            ...changedResponse,
            items: changedResponse.items.filter((item) => item.quantity > 0),
          };

          products = deDupeItemsInCart(products);

          const prevItemsCount = get().products.item_count;

          // A newer change() call for this key may have already taken over
          // (this one was aborted/superseded) - don't let a late-resolving
          // response overwrite state a newer request now owns.
          if (owns()) {
            set(
              produce((draft) => {
                draft.products = products;
                draft.error = null;
              }),
            );
          }

          if (prevItemsCount !== products.item_count) {
            await get().updateGiftWrapping();
          }

          try {
            await get().updateRecommendations();
          } catch (e) {
            // Recommendations are non-critical - don't fail the change flow
            // over a failure to fetch them.
          }
        } catch (e) {
          if (e.name === 'AbortError') throw e;

          // Try to resync with the server's authoritative cart state first
          // (e.g. the request may have partially applied); fall back to the
          // last confirmed local state if that also fails.
          let recoveredProducts = previousProducts;
          try {
            recoveredProducts = await fetchCart();
          } catch (fetchError) {
            // keep last confirmed state
          }

          if (owns()) {
            set(
              produce((draft) => {
                draft.error = e.message;
                draft.products = recoveredProducts;
              }),
            );
          }
          throw e;
        } finally {
          finish();
        }
      },
      enableRecommendations: async (intent = 'related') => {
        if (get().recommendations.enabled) return;

        set(
          produce((draft) => {
            draft.recommendations.enabled = true;
            draft.recommendations.intent = intent;
          }),
        );

        await get().updateRecommendations();
      },
      resetLatestAddedProduct: () => {
        set(
          produce((draft) => {
            draft.latestAddedProduct = null;
          }),
        );
      },
      resetVariantsBeingAdded: (variantId = '') => {
        set(
          produce((draft) => {
            draft.productVariantsBeingAdded = variantId
              ? draft.productVariantsBeingAdded.filter(
                  (id) => id !== variantId,
                )
              : [];
          }),
        );
      },
      setGiftWrapping: async (enable = true) => {
        const giftWrappingProductId = get().giftWrapping.productId;
        if (!giftWrappingProductId) return;

        const cart = get().products;
        const giftWrappingIndividual = get().giftWrapping.wrapIndividually;

        if (enable) {
          set(
            produce((draft) => {
              draft.giftWrapping.statusBeingUpdated = true;
            }),
          );

          try {
            const savedGiftWrappingProductId =
              get().products.attributes['_gift-wrapping-product-id'];
            const giftWrappingResponseData = await fetch(
              `${window.Shopify.routes.root}cart/update.js`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  attributes: {
                    ...(savedGiftWrappingProductId !== giftWrappingProductId
                      ? {
                          '_gift-wrapping-product-id': giftWrappingProductId,
                        }
                      : {}),
                    'gift-wrapping': enable,
                    'gift-wrapping-message': '',
                  },
                  updates: {
                    [giftWrappingProductId]: giftWrappingIndividual
                      ? cart.items.reduce(
                          (nonGiftWrappingQuantity, item) =>
                            item.variant_id !== giftWrappingProductId
                              ? nonGiftWrappingQuantity + item.quantity
                              : nonGiftWrappingQuantity,
                          0,
                        )
                      : 1,
                  },
                }),
              },
            );
            const products = await parseCartResponse(giftWrappingResponseData);

            set(
              produce((draft) => {
                draft.products = deDupeItemsInCart(products);
                draft.error = null;
              }),
            );
          } catch (e) {
            set(
              produce((draft) => {
                draft.error = e.message;
              }),
            );
            throw e;
          } finally {
            set(
              produce((draft) => {
                draft.giftWrapping.statusBeingUpdated = false;
              }),
            );
          }
        } else {
          const giftWrappingItem = get().products.items.find(
            (item) => item.variant_id === giftWrappingProductId,
          );

          set(
            produce((draft) => {
              draft.giftWrapping.statusBeingUpdated = true;
            }),
          );

          let signal = null;
          let finish = () => {};

          if (giftWrappingItem) {
            const lineItemUpdate = beginLineItemUpdate(giftWrappingItem.key);
            signal = lineItemUpdate.signal;
            finish = lineItemUpdate.finish;
          }

          try {
            const giftWrappingResponseData = await fetch(
              `${window.Shopify.routes.root}cart/update.js`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  attributes: {
                    '_gift-wrapping-product-id': null,
                    'gift-wrapping': enable,
                    'gift-wrapping-message': '',
                  },
                  updates: {
                    [giftWrappingProductId]: 0,
                  },
                }),
                signal,
              },
            );
            const products = await parseCartResponse(giftWrappingResponseData);

            set(
              produce((draft) => {
                draft.products = deDupeItemsInCart(products);
                draft.error = null;
              }),
            );
          } catch (e) {
            if (e.name === 'AbortError') throw e;

            set(
              produce((draft) => {
                draft.error = e.message;
              }),
            );
            throw e;
          } finally {
            set(
              produce((draft) => {
                draft.giftWrapping.statusBeingUpdated = false;
              }),
            );
            finish();
          }
        }
      },
      syncGiftWrapping: async () => {
        const giftWrappingProductId = get().giftWrapping.productId;
        const savedGiftWrappingProductId =
          get().products.attributes['_gift-wrapping-product-id'];

        if (
          savedGiftWrappingProductId &&
          savedGiftWrappingProductId !== giftWrappingProductId
        ) {
          const savedGiftWrappingProduct = get().products.items.find(
            (item) => item.id === savedGiftWrappingProductId,
          );

          if (savedGiftWrappingProduct) {
            const { signal, finish } = beginLineItemUpdate(
              savedGiftWrappingProduct.key,
            );

            try {
              const giftWrappingResponseData = await fetch(
                `${window.Shopify.routes.root}cart/update.js`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    attributes: {
                      '_gift-wrapping-product-id': null,
                    },
                    updates: {
                      [savedGiftWrappingProductId]: 0,
                    },
                  }),
                  signal,
                },
              );
              const products = await parseCartResponse(
                giftWrappingResponseData,
              );

              set(
                produce((draft) => {
                  draft.products = deDupeItemsInCart(products);
                  draft.error = null;
                }),
              );
            } catch (e) {
              if (e.name !== 'AbortError') {
                set(
                  produce((draft) => {
                    draft.error = e.message;
                  }),
                );
              }
              throw e;
            } finally {
              finish();
            }
          }
        }

        if (!giftWrappingProductId) return;

        const giftWrappingEnabled = Boolean(
          get().products.attributes['gift-wrapping'],
        );
        const giftWrappingIndividual = get().giftWrapping.wrapIndividually;
        const giftWrappingItem = get().products.items.find(
          (item) => item.variant_id === giftWrappingProductId,
        );

        if (!giftWrappingEnabled && giftWrappingItem) {
          await get().setGiftWrapping(false);
        }

        if (giftWrappingEnabled && !giftWrappingItem) {
          await get().setGiftWrapping(true);
        }

        if (
          giftWrappingEnabled &&
          giftWrappingItem &&
          giftWrappingIndividual &&
          giftWrappingItem.quantity === 1
        ) {
          await get().updateGiftWrapping();
        }

        if (
          giftWrappingEnabled &&
          giftWrappingItem &&
          !giftWrappingIndividual &&
          giftWrappingItem.quantity > 1
        ) {
          await get().updateGiftWrapping();
        }
      },
      updateGiftWrapping: async () => {
        const giftWrappingProductId = get().giftWrapping.productId;
        if (!giftWrappingProductId) return;

        const giftWrappingIndividual = get().giftWrapping.wrapIndividually;
        const giftWrappingItem = get().products.items.find(
          (item) => item.variant_id === giftWrappingProductId,
        );

        if (
          !giftWrappingItem ||
          (get().products.items_count > 1 &&
            !giftWrappingIndividual &&
            giftWrappingItem)
        )
          return;

        const key = giftWrappingItem.key;
        const { signal, finish } = beginLineItemUpdate(key);

        try {
          let giftWrappingQuantity = giftWrappingIndividual
            ? get().products.items.reduce(
                (nonGiftWrappingQuantity, item) =>
                  item.variant_id !== giftWrappingProductId
                    ? nonGiftWrappingQuantity + item.quantity
                    : nonGiftWrappingQuantity,
                0,
              )
            : 1;

          if (
            giftWrappingItem &&
            get().products.items.filter((i) => i.id !== giftWrappingProductId)
              .length === 0
          ) {
            giftWrappingQuantity = 0;
          }

          const giftWrappingAttributeUpdate =
            giftWrappingQuantity === 0
              ? {
                  attributes: {
                    'gift-wrapping': false,
                    'gift-wrapping-message': '',
                  },
                }
              : {};

          const data = await fetch(
            `${window.Shopify.routes.root}cart/update.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...giftWrappingAttributeUpdate,
                updates: {
                  [giftWrappingProductId]: giftWrappingQuantity,
                },
              }),
              signal,
            },
          );
          const cart = await parseCartResponse(data);

          set(
            produce((draft) => {
              draft.products = deDupeItemsInCart(cart);
              draft.error = null;
            }),
          );
        } catch (e) {
          if (e.name === 'AbortError') throw e;

          set(
            produce((draft) => {
              draft.error = e.message;
            }),
          );
          throw e;
        } finally {
          finish();
        }
      },
      updateGiftWrappingMessage: async (message) => {
        if (
          get().giftWrapping.messageBeingUpdated &&
          get().ongoingUpdates.giftWrappingMessage
        ) {
          get().ongoingUpdates.giftWrappingMessage.abort();
          set(
            produce((draft) => {
              draft.ongoingUpdates.giftWrappingMessage = null;
              draft.giftWrapping.messageBeingUpdated = false;
            }),
          );
        }

        const controller = new AbortController();
        const signal = controller.signal;

        set(
          produce((draft) => {
            draft.ongoingUpdates.giftWrappingMessage = controller;
            draft.giftWrapping.messageBeingUpdated = true;
          }),
        );

        try {
          const updatedMessageResponse = await fetch(
            `${window.Shopify.routes.root}cart/update.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                attributes: {
                  'gift-wrapping-message': message,
                },
              }),
              signal,
            },
          );
          const products = await parseCartResponse(updatedMessageResponse);

          set(
            produce((draft) => {
              draft.products = deDupeItemsInCart(products);
              draft.error = null;
            }),
          );
        } catch (e) {
          if (e.name === 'AbortError') throw e;

          set(
            produce((draft) => {
              draft.error = e.message;
            }),
          );
          throw e;
        } finally {
          set(
            produce((draft) => {
              if (get().ongoingUpdates.giftWrappingMessage === controller) {
                draft.ongoingUpdates.giftWrappingMessage = null;
                draft.giftWrapping.messageBeingUpdated = false;
              }
            }),
          );
        }
      },
      updateNote: async (note) => {
        if (get().noteBeingUpdated && get().ongoingUpdates.note) {
          get().ongoingUpdates.note.abort();
          set(
            produce((draft) => {
              draft.ongoingUpdates.note = null;
              draft.noteBeingUpdated = false;
            }),
          );
        }

        const controller = new AbortController();
        const signal = controller.signal;

        set(
          produce((draft) => {
            draft.ongoingUpdates.note = controller;
            draft.noteBeingUpdated = true;
          }),
        );

        try {
          const updatedNoteResponse = await fetch(
            `${window.Shopify.routes.root}cart/update.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                note,
              }),
              signal,
            },
          );
          const products = await parseCartResponse(updatedNoteResponse);

          set(
            produce((draft) => {
              draft.products = deDupeItemsInCart(products);
              draft.error = null;
            }),
          );
        } catch (e) {
          if (e.name === 'AbortError') throw e;

          set(
            produce((draft) => {
              draft.error = e.message;
            }),
          );
          throw e;
        } finally {
          set(
            produce((draft) => {
              if (get().ongoingUpdates.note === controller) {
                draft.ongoingUpdates.note = null;
                draft.noteBeingUpdated = false;
              }
            }),
          );
        }
      },
      updateRecommendations: async () => {
        // TODO: Use local storage to cache recommendations
        if (!get().recommendations.enabled) return;

        // Can be either 'proportional' or 'equal'
        const weightDistribution = 'equal';
        const intent = get().recommendations.intent;
        const newRecommendations = {};
        const existingRecommendations = get().recommendations.items;
        const existingRecommendationsIds = Object.keys(
          existingRecommendations,
        );
        const productIds = get().products.items.reduce((ids, item) => {
          // TODO: Also exclude gift wrapping product id
          if (!item.gift_card && !ids.includes(item.product_id.toString()))
            ids.push(item.product_id.toString());

          return ids;
        }, []);

        const remainingProductIds = existingRecommendationsIds.filter(
          (productId) => productIds.includes(productId),
        );

        const remainingRecommendations = remainingProductIds.reduce(
          (recommendations, productId) => {
            return {
              ...recommendations,
              [productId]: existingRecommendations[productId],
            };
          },
          {},
        );

        const newProductIds = productIds.filter(
          (productId) => !existingRecommendationsIds.includes(productId),
        );

        // Products in the cart are excluded from the recommendations
        // so when the product is removed from the cart, the recommendations
        // should be updated, together with fetching ones for new products
        await Promise.all(
          [
            ...(remainingProductIds.length < existingRecommendationsIds.length
              ? remainingProductIds
              : []),
            ...newProductIds,
          ].map(async (productId) => {
            const data = await fetch(
              `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&intent=${intent}`,
            );
            if (!data.ok) return;

            const recommendations = await data.json();

            if (remainingProductIds.includes(productId)) {
              remainingRecommendations[productId] = recommendations;
            } else {
              newRecommendations[productId] = recommendations;
            }
          }),
        );

        const finalRecommendations = {
          ...remainingRecommendations,
          ...newRecommendations,
        };

        // Calculate and set weight
        const calculateWeight = (productId) => {
          const totalPrice = get()
            .products.items.filter(
              (item) => item.product_id.toString() === productId,
            )
            .reduce((total, item) => {
              return total + item.line_price;
            }, 0);

          return totalPrice / get().products.total_price;
        };

        set(
          produce((draft) => {
            draft.recommendations.items = Object.keys(
              finalRecommendations,
            ).reduce((recommendations, productId) => {
              recommendations[productId] = {
                weight:
                  weightDistribution === 'proportional'
                    ? calculateWeight(productId)
                    : 1 /
                      get().products.items.reduce((items, item) => {
                        if (!items.includes(item.id)) items.push(item.id);

                        return items;
                      }, []).length,
                products: finalRecommendations[productId].products,
              };

              return recommendations;
            }, {});
          }),
        );
      },
    };
  });
};

initiateStore();
resetFreeShippingAnimation();

try {
  await window.theme.cart.store.getState().syncGiftWrapping();
} catch (e) {
  // Already recorded on the store's `error` field - don't let a failed
  // gift-wrapping sync abort evaluation of this module and break the cart.
}
